import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';

import { WikiService } from '../services/wiki.service';
import { gfRelativeDateFunction } from '../../../core/pipes/date/date.functions';

/** The 409 body returned by POST /api/wiki when `baseVersion` is stale. */
export interface WikiConflictInfo {
    error: string;
    pageId: number;
    pagename: string;
    baseVersion: number;
    currentVersion: number;
    currentAuthor: { id: number; unixName: string; firstname: string; lastname: string } | null;
    currentSavedAt: string | null;
}

/**
 * Shown when a save is rejected because someone else saved the page first.
 */
@Component({
    selector: 'wiki-conflict-modal',
    template: require('./wiki.conflict-modal.component.html')
})
export class WikiConflictModalComponent implements OnInit {
    /** Conflict payload from the 409 response. */
    @Input() conflict: WikiConflictInfo;
    /** Page id used for the diff lookup. Falls back to `conflict.pageId`. */
    @Input() wikiId: number = 0;
    /** When false, the "Save anyway" button is hidden (e.g. read-only reviewers). */
    @Input() allowOverwrite: boolean = true;

    diff: any = null;
    loadingDiff: boolean = false;
    diffError: boolean = false;
    diffRequested: boolean = false;

    /**
     * When the conflicting version was saved, formatted for display — "Today at 10:14 AM" for a
     * recent save, falling back to a plain date for an older one. Empty when the server reported
     * no usable timestamp, which the template uses to drop the separator too.
     *
     * Computed once rather than piped in the template: the conflict cannot change while the
     * modal is open, so re-running moment on every change-detection pass buys nothing.
     */
    savedAtDisplay: string = '';

    constructor(public activeModal: NgbActiveModal, private wikiService: WikiService) {}

    ngOnInit(): void {
        if (!this.wikiId) { this.wikiId = _.get(this.conflict, 'pageId', 0); }
        // Same formatting the Versions tab uses for version timestamps, so the two agree.
        this.savedAtDisplay = gfRelativeDateFunction(_.get(this.conflict, 'currentSavedAt', ''), undefined) || '';
    }

    /** "Jane Doe", "jdoe", or a generic fallback — the author lookup is best-effort server-side. */
    get authorName(): string {
        const author = this.conflict?.currentAuthor ?? null;
        if (!author) { return ''; }
        const full = [author.firstname, author.lastname].filter(Boolean).join(' ').trim();
        return full || author.unixName || '';
    }

    /** Fetch the base..current diff on demand so the modal opens instantly. */
    showDiff(): void {
        this.diffRequested = true;
        this.loadingDiff = true;
        this.diffError = false;

        this.wikiService.diff(this.wikiId, {
            version1: this.conflict.baseVersion,
            version2: this.conflict.currentVersion,
        })
            .then((response: any) => {
                this.diff = _.get(response, 'diff', []);
                this.loadingDiff = false;
            })
            .catch(() => {
                this.diffError = true;
                this.loadingDiff = false;
            });
    }

    /** Re-save on top of the other person's version. Nothing is lost — both stay in history. */
    onOverwrite(): void {
        this.activeModal.close('overwrite');
    }

    onKeepEditing(): void {
        this.activeModal.close('keep-editing');
    }
}
